import type { ReactElement } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useToasts } from "app/common/hooks";
import {
    StyledLeadsPageActionsWrapper,
    StyledLeadsPageHeaderWrapper,
} from "app/pages/sales-intelligence/common-components/layout";
import useSetTimeout from "app/pages/sales-intelligence/hooks/useSetTimeout";
import { useResetAllFilters } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useResetAllFilters";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import { useActiveListOption } from "app/pages/sales-intelligence/modules/lists/hooks/useActiveListOption";
import { useCompaniesListData } from "app/pages/sales-intelligence/modules/lists/hooks/useCompaniesListData";
import { listsQueryKeys } from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/services/queries/queryKeyFactory";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import { HiddenCompaniesToast } from "app/pages/sales-intelligence/pages/opportunity-list/components/HiddenCompaniesToast/HiddenCompaniesToast";
import { ExcludeDuplicateOpportunitiesModalContainer } from "app/pages/sales-intelligence/pages/opportunity-list/containers/ExcludeDuplicateOpportunitiesModal/ExcludeDuplicateOpportunitiesModalContainer";
import useInvalidateOpportunityQueries from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useInvalidateOpportunityQueries";
import { shouldShowExcludeDuplicateOpportunitiesModalThunk } from "app/pages/sales-intelligence/pages/opportunity-list/store/effects";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import AddWebsitesDropdownContainer from "app/pages/sales-intelligence/sub-modules/opportunities/components/top-section/ControlsSection/AddWebsitesDropdownContainer";
import ListDeleteModalContainer from "app/pages/sales-intelligence/sub-modules/opportunities/containers/ListDeleteModal/ListDeleteModalContainer";
import ListModalWithDuplicateOpportunitiesContainer from "app/pages/sales-intelligence/sub-modules/opportunities/containers/ListModal/ListModalWithDuplicateOpportunitiesContainer";
import { ListOptionsDropdownTrackedContainer } from "app/pages/sales-intelligence/sub-modules/opportunities/containers/ListOptionsDropdown/ListOptionsDropdownTrackedContainer";
import ListRenameModalContainer from "app/pages/sales-intelligence/sub-modules/opportunities/containers/ListRenameModal/ListRenameModalContainer";
import * as actionCreators from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import { removeOpportunityListAction } from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import {
    selectGroupedOpportunityListOptions,
    selectIsRecentlyViewedOpportunityList,
} from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type {
    AddOpportunitiesToListDto,
    OpportunityListType,
} from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import { OpportunityListVariant } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import { isSignalsManagementOpenAction } from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";

import { StyledPageTitleDropdown } from "../../components/PageTitleButton/styles";
import StaticListPageTitleButtonContainer from "../PageTitleButton/StaticListPageTitleButtonContainer";
import WithEducationBar from "../WithEducationBar/WithEducationBar";

const StaticListPageHeaderContainer = (): ReactElement => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { translate } = useTranslate();

    const { id: listId } = useListParams();

    const activeListOption = useActiveListOption();

    const invalidateOpportunitiesQueries = useInvalidateOpportunityQueries();

    const { getData: getListData } = useCompaniesListData();

    const groupedLists = useSelector(selectGroupedOpportunityListOptions);

    const { showSuccessToast } = useToasts();
    const resetAllFilters = useResetAllFilters();

    const isOnlyOneList =
        [...groupedLists[OpportunityListVariant.Static], ...groupedLists[OpportunityListVariant.Crm]].length === 1;

    const isRecentlyViewedList = useSelector((state) => selectIsRecentlyViewedOpportunityList(state, listId));

    const setTimeout = useSetTimeout();

    const handleAfterListDelete = (listId: OpportunityListType["opportunityListId"]): void => {
        void siNavigator.goToListsHomePage({ location: "replace" });
        void queryClient.invalidateQueries({ queryKey: listsQueryKeys.allStaticLists(), refetchType: "all" });

        setTimeout(() => {
            dispatch(removeOpportunityListAction(listId));
        }, 0);
    };

    const addWebsitesToList = async (list: Readonly<OpportunityListType>, dto: AddOpportunitiesToListDto) => {
        const shouldToggleModal = await dispatch(
            shouldShowExcludeDuplicateOpportunitiesModalThunk(list.opportunityListId, dto.opportunities),
        );
        if (shouldToggleModal) {
            dispatch(actionCreators.toggleExcludeListsDuplicatesModal(true));
        } else {
            await invalidateOpportunitiesQueries(list.opportunityListId);
            const listDomains = getListData().map((item) => item.domain);

            const addedDomains = dto.opportunities;

            const hasMissingDomains = addedDomains.some((domain) => !listDomains.includes(domain));

            if (hasMissingDomains) {
                showSuccessToast(<HiddenCompaniesToast onReset={resetAllFilters} />);
            } else {
                const toastTextKey =
                    addedDomains.length > 1
                        ? "si.pages.search_result.add_leads_to_list.success.plural"
                        : "si.pages.search_result.add_leads_to_list.success";

                showSuccessToast(translate(toastTextKey));
            }
        }
    };

    const handleOpenSignals = (): void => {
        dispatch(isSignalsManagementOpenAction(true));
    };

    return (
        <WithEducationBar>
            {({ educationBarButton }) => (
                <StyledLeadsPageHeaderWrapper>
                    <Flex
                        flex={1}
                        minWidth={0}
                        overflow="visible"
                    >
                        {activeListOption ? (
                            isOnlyOneList ? (
                                <StaticListPageTitleButtonContainer list={activeListOption} />
                            ) : (
                                <StyledPageTitleDropdown
                                    selectedList={activeListOption}
                                    renderDropdownButton={StaticListPageTitleButtonContainer}
                                />
                            )
                        ) : null}

                        <ListOptionsDropdownTrackedContainer />
                        <ListRenameModalContainer />
                        <ListDeleteModalContainer onAfterDelete={handleAfterListDelete} />
                    </Flex>

                    <StyledLeadsPageActionsWrapper>
                        <DataCreditsIndicatorContainer />
                        {isRecentlyViewedList ? null : (
                            <>
                                <IconButton
                                    type="flat"
                                    iconName="settings"
                                    onClick={handleOpenSignals}
                                >
                                    {translate("si.signals.dropdown.button.manage.text")}
                                </IconButton>
                                <AddWebsitesDropdownContainer />
                                <ListModalWithDuplicateOpportunitiesContainer
                                    handleAddWebsitesToList={addWebsitesToList}
                                />
                                <ExcludeDuplicateOpportunitiesModalContainer />
                            </>
                        )}

                        {educationBarButton}
                    </StyledLeadsPageActionsWrapper>
                </StyledLeadsPageHeaderWrapper>
            )}
        </WithEducationBar>
    );
};

export default StaticListPageHeaderContainer;
